// src/utils/sentinel.js
// Utility for cross-browser focus trap using invisible sentinels before and after a modal/dialog
// Usage: const removeTrap = addSentinelFocusTrap(modalElement);
// Call removeTrap() when the modal is closed to clean up sentinels.

export function addSentinelFocusTrap(modalElement) {
  // Check if modalElement is attached to DOM
  if (!modalElement || !modalElement?.parentNode) {
    console.warn('addSentinelFocusTrap: modalElement is not attached to DOM');
    return () => {}; // Return no-op cleanup function
  }

  // Remove any existing sentinels first
  if (modalElement._sentinelStart?.parentNode) {
    modalElement._sentinelStart.parentNode.removeChild(modalElement._sentinelStart);
  }
  if (modalElement._sentinelEnd?.parentNode) {
    modalElement._sentinelEnd.parentNode.removeChild(modalElement._sentinelEnd);
  }

  // Create start and end sentinels
  const sentinelStart = document.createElement('div');
  sentinelStart.setAttribute('tabindex', '0');
  sentinelStart.style.position = 'absolute';
  sentinelStart.style.width = '1px';
  sentinelStart.style.height = '1px';
  sentinelStart.style.overflow = 'hidden';
  sentinelStart.style.opacity = '0';
  sentinelStart.style.pointerEvents = 'none';
  sentinelStart.setAttribute('aria-hidden', 'true');

  const sentinelEnd = document.createElement('div');
  sentinelEnd.setAttribute('tabindex', '0');
  sentinelEnd.style.position = 'absolute';
  sentinelEnd.style.width = '1px';
  sentinelEnd.style.height = '1px';
  sentinelEnd.style.overflow = 'hidden';
  sentinelEnd.style.opacity = '0';
  sentinelEnd.style.pointerEvents = 'none';
  sentinelEnd.setAttribute('aria-hidden', 'true');

  // Insert sentinels before and after the modal
  modalElement.parentNode.insertBefore(sentinelStart, modalElement);
  modalElement.parentNode.insertBefore(sentinelEnd, modalElement.nextSibling);
  modalElement._sentinelStart = sentinelStart;
  modalElement._sentinelEnd = sentinelEnd;

  // Focus logic for sentinels - using consistent selector from _setInitialFocus
  const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  sentinelStart.addEventListener('focus', () => {
    const focusableElements = modalElement.querySelectorAll(focusableSelector);
    const last = focusableElements[focusableElements.length - 1];
    if (last) {
      last.focus();
    }
  });
  sentinelEnd.addEventListener('focus', () => {
    const focusableElements = modalElement.querySelectorAll(focusableSelector);
    const first = focusableElements[0];
    if (first) {
      first.focus();
    }
  });

  // Remove sentinels when modal is closed
  return () => {
    if (sentinelStart.parentNode) {
      sentinelStart.parentNode.removeChild(sentinelStart);
    }
    if (sentinelEnd.parentNode) {
      sentinelEnd.parentNode.removeChild(sentinelEnd);
    }
  };
}
